import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/data/page-header'
import { FlagChips, PlanBadge, StateBadge } from '@/components/data/badges'
import { FilterSelect, PAGE_SIZE, Pagination, SearchBox, SortHead } from '@/components/data/list-controls'
import { MonoId } from '@/components/data/mono-id'
import { EmptyState, ErrorState, SkeletonRows, Warnings } from '@/components/data/states'
import { RenewsCell, TeamNameCell } from '@/components/data/team-cells'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useListParams } from '@/hooks/use-list-params'
import { formatMoney } from '@/lib/format'
import { stateLabels } from '@/lib/labels'
import { useSubscriptions } from '@/lib/queries'
import type { SubscriptionStateName } from '@/lib/types'

const defaults = { sort: 'expires', order: 'asc' }
const STATES: SubscriptionStateName[] = ['active', 'expiring', 'canceling', 'expired', 'free']

export default function SubscriptionsPage() {
  const { params, set } = useListParams(defaults)
  const { data, error, isPending, isFetching, refetch } = useSubscriptions(params)
  const offset = Number(params.offset ?? 0)
  const totals = data?.totals
  const sortProps = { sort: params.sort, order: params.order, onSort: (sort: string, order: string) => set({ sort, order }) }

  const strip: { state: SubscriptionStateName | ''; label: string; value: number | undefined; tone?: string }[] = [
    { state: 'active', label: 'Active', value: totals?.active, tone: 'text-ok' },
    { state: 'expiring', label: 'Renew within 14 days', value: totals?.expiring, tone: 'text-warn' },
    { state: 'canceling', label: 'Cancelling', value: totals?.canceling, tone: 'text-warn' },
    { state: 'expired', label: 'Ended', value: totals?.expired, tone: 'text-risk' },
    { state: 'free', label: 'On Free', value: totals?.free },
  ]

  return (
    <TooltipProvider>
      <PageHeader title="Subscriptions">
        {totals ? (
          <>
            About <span className="figures font-medium text-foreground">{formatMoney(totals.mrr_cents)}</span> a month at list price from live subscriptions.
          </>
        ) : (
          'Paid plans and when they renew.'
        )}
      </PageHeader>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5" role="group" aria-label="Filter by state">
        {strip.map((s) => {
          const on = params.state === s.state
          return (
            <button
              key={s.state}
              type="button"
              aria-pressed={on}
              onClick={() => set({ state: on ? '' : s.state })}
              className={cn('panel p-3 text-left hover:bg-accent/40', on && 'ring-2 ring-primary/50')}
            >
              <div className="text-muted-foreground">{s.label}</div>
              <div className={cn('figures text-xl font-semibold', s.tone)}>{s.value ?? '–'}</div>
            </button>
          )
        })}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchBox value={params.search ?? ''} onChange={(search) => set({ search })} placeholder="Team, owner, Stripe id" />
        <FilterSelect
          label="Plan"
          value={params.plan ?? ''}
          onChange={(plan) => set({ plan })}
          options={[{ value: 'premium_monthly', label: 'Premium monthly' }, { value: 'premium_yearly', label: 'Premium yearly' }]}
        />
        <FilterSelect label="State" value={params.state ?? ''} onChange={(state) => set({ state })} options={STATES.map((s) => ({ value: s, label: stateLabels[s] }))} />
      </div>

      <Warnings warnings={data?.warnings} />

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <SortHead id="name" {...sortProps}>
                  Team
                </SortHead>
                <TableHead>Plan</TableHead>
                <SortHead id="expires" {...sortProps}>
                  Renews
                </SortHead>
                <SortHead id="plan" {...sortProps} align="right">
                  Per month
                </SortHead>
                <TableHead>Stripe customer</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className={isFetching && !isPending ? 'opacity-70' : undefined}>
              {isPending ? (
                <SkeletonRows columns={6} />
              ) : (
                data?.items.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="max-w-64">
                      <Link to={`/teams/${t.id}?tab=billing`} className="block hover:text-primary">
                        <TeamNameCell team={t} />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <PlanBadge planId={t.plan_id} name={t.plan_name} trial={t.subscription.source === 'trial'} />
                        <StateBadge state={t.subscription.state} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <RenewsCell team={t} />
                    </TableCell>
                    <TableCell className="figures text-right">{t.subscription.monthly_cents ? formatMoney(t.subscription.monthly_cents) : '—'}</TableCell>
                    <TableCell className="max-w-44">
                      {t.subscription.stripe_customer_id ? (
                        <span className="inline-flex max-w-full items-center gap-1.5">
                          <MonoId value={t.subscription.stripe_customer_id} />
                          <a
                            href={`https://dashboard.stripe.com/customers/${t.subscription.stripe_customer_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="shrink-0 text-muted-foreground hover:text-primary"
                            aria-label="Open in Stripe"
                          >
                            <ExternalLink className="size-3.5" />
                          </a>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <FlagChips flags={t.flags.filter((f) => ['payment_issue', 'subscription_expired', 'subscription_expiring', 'cancel_pending'].includes(f)) as typeof t.flags} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {error && <ErrorState error={error} onRetry={() => refetch()} />}
        {!isPending && !error && data?.items.length === 0 && (
          <EmptyState title="No subscriptions here">{params.state || params.plan || params.search ? 'Remove a filter to see more.' : 'No team has a paid plan yet.'}</EmptyState>
        )}
        <Pagination total={data?.total ?? 0} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => set({ offset: String(o) })} />
      </div>
    </TooltipProvider>
  )
}
